'use client';

import { clsx } from 'clsx';
import { capitalize } from 'lodash';
import { FC, useState } from 'react';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { Body, Info, NumberText, Title } from 'atoms/Typography/Typography';
import { ButtonWithIcon } from 'molecules/ButtonWithIcon';
import { DeliveryStatus, DeliveryStatusType } from 'molecules/DeliveryStatus';
import { Price } from 'molecules/Price';
import { ProductReferencePrice } from 'molecules/ProductReferencePrice';
import { ReviewsRating } from 'molecules/ReviewsRating';
import { VariantList } from 'molecules/VariantList';
import { Vat } from 'molecules/Vat';
import { WishlistIcon } from 'molecules/WishlistIcon';
import {
  PriceInfo,
  ProductQuantitySelect,
} from 'organisms/ProductPriceSection';
import { ProductDiscount } from 'organisms/ProductPriceSection/PriceInfo';
import { useProductSizeSelect } from 'organisms/ProductPriceSection/useProductSizeSelect';
import { ProductSizesProvider } from 'pages/ProductDetail/context';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useRouter } from 'modules/next/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import {
  IAdaptedBasicProduct,
  IAdaptedProduct,
} from 'commerce/scayle/utils/adapter/types';
import styles from './styles.module.css';

export const ListicleTile: FC<{
  bapiProduct: IAdaptedProduct;
  index: number;
}> = ({ bapiProduct: parsedProduct, index }) => {
  const t = useTranslations();
  const { isMobile, isTablet } = useMediaQuery();
  const { gtmData } = useSiteConfig();
  const { push } = useRouter();

  const [state, setState] = useState({
    quantity: 1,
    listicleProductRefKey: parsedProduct.referenceKey,
  });

  const siblings = parsedProduct.siblings || [];
  const productWithSiblingsByRefKey: Record<string, IAdaptedBasicProduct> = {
    [parsedProduct.referenceKey]: parsedProduct,
  };

  siblings.forEach(
    (sibling) => (productWithSiblingsByRefKey[sibling.referenceKey] = sibling),
  );

  const product = productWithSiblingsByRefKey[state.listicleProductRefKey];
  const { addToCart } = useCart();

  const {
    hasSizes,
    sizeOptions,
    selectedVariant,
    quantityLimit,
    setSelectedVariant,
  } = useProductSizeSelect(product, product.referenceKey, true);

  const activeVariant =
    product.variants?.find(
      (variant) => variant.referenceKey === selectedVariant,
    ) || product.variants?.[0];

  const isOutOfStock =
    !activeVariant?.stock || activeVariant.stock.quantity === 0;
  const isDisabled = isOutOfStock || (hasSizes && !selectedVariant);

  const sortedVariants = Object.values(productWithSiblingsByRefKey)
    .sort((a, b) => a.href.localeCompare(b.href))
    .filter((variant) => !variant.isSoldOut)
    .map((variant) => ({
      ...variant,
      title: capitalize(variant?.colors?.[0]?.label),
      cover: variant?.coverImage,
    }));

  const transformedSizeOptions = sizeOptions.map((option) => ({
    ...option,
    label:
      option.disabled && option.value
        ? t('product.pdp.soldOut', { label: option.label || '' })
        : option.label,
  }));

  const handleClick = async () => {
    if (!activeVariant?.referenceKey) {
      return;
    }

    if (hasSizes && !selectedVariant) {
      return;
    }

    addToCart(
      activeVariant.referenceKey,
      state.quantity,
      gtmData?.pageType,
      product.limitForCart,
    );
  };

  const productIndex = String(index + 1).padStart(2, '0');

  const setActiveVariant = (variantReferenceKey: string) => {
    setState((s) => ({
      ...s,
      listicleProductRefKey: variantReferenceKey,
    }));
  };

  const selectedSizeLabel = transformedSizeOptions.find(
    (option) => option.value === selectedVariant,
  )?.label;

  return (
    <ProductSizesProvider product={product}>
      <div className={styles.root}>
        <NumberText className={styles.productIndex}>{productIndex}</NumberText>
        <div
          className={styles.productInfo}
          onClick={() => !isTablet && push(product.href)}
        >
          <Link href={product.href} className={styles.imageWrapper}>
            <Image
              src={product.coverImage}
              alt={product.name}
              className={styles.image}
            />
          </Link>
          {isMobile && product.label && (
            <Body type="md" className={styles.label}>
              {product.label}
            </Body>
          )}
          {isMobile && (
            <WishlistIcon
              className={styles.wishlist}
              productName={product.name}
              variantId={product?.variants?.[0].id}
            />
          )}
          <div className={styles.content}>
            {isTablet && (
              <div className={styles.top}>
                <>
                  <Body
                    type="md"
                    className={clsx(
                      styles.label,
                      !product.label && styles.hidden,
                    )}
                  >
                    {product.label}
                  </Body>
                  <DeliveryStatus status={DeliveryStatusType.AVAILABLE} />
                </>
              </div>
            )}
            {isMobile && (
              <ReviewsRating
                value={parsedProduct?.ratingScore}
                count={parsedProduct?.ratingCount}
                url={product.href}
                color="gray"
                className={styles.reviews}
              />
            )}
            <div className={styles.textContainer}>
              <Body type="sm" className={styles.brand}>
                {product.brand}
              </Body>
              <Link href={product.href} className={styles.name}>
                <Title type="md">
                  <ParseBBCode content={product.name} />
                </Title>
              </Link>
            </div>

            {isTablet && (
              <>
                <ReviewsRating
                  value={parsedProduct?.ratingScore}
                  count={parsedProduct?.ratingCount}
                  url={product.href}
                  color="gray"
                />

                {product.price?.reference && (
                  <ProductReferencePrice
                    price={product.price}
                    className={styles.measurable}
                  />
                )}

                {hasSizes && (
                  <div className={styles.sizes}>
                    <Info>
                      {t('basket.texts.size', {
                        value: String(selectedSizeLabel),
                      })}
                    </Info>
                    <div className={styles.sizeItemsWrapper}>
                      {transformedSizeOptions
                        .filter((option) => !option.disabled)
                        .map((size) => (
                          <Info
                            onClick={() =>
                              setSelectedVariant(String(size.value))
                            }
                            className={clsx(
                              styles.sizeItem,
                              size.value === selectedVariant && styles.isActive,
                            )}
                            key={size.label}
                          >
                            {size.label}
                          </Info>
                        ))}
                    </div>
                  </div>
                )}

                <div
                  className={clsx(
                    styles.colors,
                    !(sortedVariants.length > 1) && styles.hidden,
                  )}
                >
                  <VariantList
                    variants={sortedVariants}
                    isLink={false}
                    setActiveVariant={setActiveVariant}
                    currentProductId={String(product?.id)}
                  />
                </div>
                <PriceInfo type="column" className={styles.priceInfo}>
                  <ProductDiscount
                    discountPercentage={product.discountPercentage}
                    recommendedPrice={product.price.recommendedRetailPrice}
                    currencyCode={product.price.currencyCode}
                    className={styles.discount}
                  />
                  <Price
                    amount={activeVariant?.price.withTax}
                    currencyCode={activeVariant?.price.currencyCode}
                    isCrossed={false}
                    size="regular"
                  />
                </PriceInfo>
                <Vat rate={product.taxRatePercent} className={styles.vat} />
                <div className={styles.selectors}>
                  <ProductQuantitySelect
                    quantityLimit={quantityLimit}
                    setQuantity={(quantity) =>
                      setState((s) => ({ ...s, quantity }))
                    }
                    isOutOfStock={isDisabled}
                    className={styles.selector}
                  />
                  <ButtonWithIcon
                    color="deep-purple"
                    size="large"
                    iconSvgType="cartWhite"
                    onClick={handleClick}
                    disabled={isDisabled}
                  >
                    {t('common.buttons.buy')}
                  </ButtonWithIcon>
                </div>
              </>
            )}
          </div>
          {isMobile && (
            <PriceInfo type="column" className={styles.priceInfo}>
              <ProductDiscount
                discountPercentage={product.discountPercentage}
                recommendedPrice={product.price.recommendedRetailPrice}
                currencyCode={product.price.currencyCode}
                className={styles.discount}
              />
              <Price
                amount={activeVariant?.price.withTax}
                currencyCode={activeVariant?.price.currencyCode}
                isCrossed={false}
                size="regular"
              />
            </PriceInfo>
          )}
        </div>
      </div>
    </ProductSizesProvider>
  );
};
